import React, { Suspense, useEffect, useMemo, memo } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { getAllRolesInBoolean, getIsTeacherOrCreatorData } from 'src/mentor/layout/utils'
import { primaryRoutes } from 'src/mentor/routes'
import { ERMOptionsSyncComponent } from '../MentorSettingsPage/MentorSettingsComponents'
import MentorFooter from '../MentorFooter/MentorFooter'
import { ContentContainer } from './MentorHeaderAndContentStyles'

function MentorHeaderAndContent({ removeLeftMargins }) {
  const navigate = useNavigate()
  const location = useLocation()
  const teacherCreatorRoleData = getIsTeacherOrCreatorData()
  
  
  // Extract pathname to avoid location object reference changes
  const pathname = useMemo(() => location?.pathname, [location?.pathname])

  // Memoize teacherCreatorRoleData to prevent infinite loops
  const isCreatorOnly = useMemo(
    () => !teacherCreatorRoleData?.isTeacher && teacherCreatorRoleData?.isCreator,
    [teacherCreatorRoleData?.isTeacher, teacherCreatorRoleData?.isCreator],
  )

  // Only redirect creator-only users if not already on profile page
  // Don't redirect if on kyc-pending-actions (let MentorLayout handle that redirect)
  useEffect(() => {
    const isKycPendingPage = pathname?.includes('kyc-pending-actions')
    const isProfilePage = pathname === '/mentor-home/profile' || pathname === '/mentor-home/profile/'
    
    if (isCreatorOnly && !isProfilePage && !isKycPendingPage) {
      navigate('/mentor-home/profile', { replace: true })
    }
  }, [pathname, isCreatorOnly, navigate])

  // Memoize routes to prevent remounting child components on every render
  const routes = useMemo(
    () =>
      primaryRoutes?.map(({ path, name, Element, exact, ...props }, idx) => {
        return (
          Element && (
            <Route
              key={idx}
              path={path}
              exact={exact}
              name={name}
              element={<Element {...props} />}
            />
          )
        )
      }),
    [], // primaryRoutes is imported constant, so it shouldn't change
  )

  const showFooter = useMemo(() => {
    if (pathname?.includes('join-chamber-room')) return false
    // Clinic-portal pages render a full-screen layout with a fixed left rail (incl. its
    // "Help & Calls" button) and already expose the portal switcher in their header. The
    // quote footer would overlap that rail, so hide it on those routes.
    const clinicPortalPrefixes = [
      '/mentor-home/my-clinic-associations',
      '/mentor-home/stats',
      '/mentor-home/manage-my-bookings',
      '/mentor-home/preferences-and-settings',
    ]
    if (clinicPortalPrefixes.some((prefix) => pathname?.startsWith(prefix))) return false
    return true
  }, [pathname])

  return (
    <ContentContainer removeLeftMargins={removeLeftMargins}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>{routes}</Routes>
      </Suspense>
      {(getAllRolesInBoolean().isDoctor ||
        getAllRolesInBoolean().isTherapist ||
        getAllRolesInBoolean().isScreener) && <ERMOptionsSyncComponent takeActionsItself={true} />}
      {showFooter && <MentorFooter />}
    </ContentContainer>
  )
}

// Memoize to prevent re-renders when only location object reference changes
// Note: This component uses useLocation() internally, so it will still re-render on location changes
// but the memo prevents re-renders when only the parent re-renders with same props
export default memo(MentorHeaderAndContent)
